import { useState, useCallback } from "react";
import { consumersApi } from "../API/consumersApi";

export const useConsumerApi = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notes, setNotes] = useState([]);
  // Get a consumer by ID
  const getConsumerById = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const result = await consumersApi.getConsumer(id);
      if (result.error) {
        setError(result.error);
        return null;
      }
      return result;
    } catch (err) {
      setError("Failed to get consumer");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Get consumers by client
  const getConsumersByClient = useCallback(async (clientId) => {
    setLoading(true);
    setError(null);
    try {
      const result = await consumersApi.getConsumersByClient(clientId);
      if (result.error) {
        setError(result.error);
        return null;
      }
      return result;
    } catch (err) {
      setError("Failed to get consumers");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Get consumer by email and client
  const getConsumerByEmail = useCallback(async (email, clientId) => {
    setLoading(true);
    setError(null);
    try {
      const result = await consumersApi.getConsumerByEmail(email, clientId);
      if (result.error) {
        setError(result.error);
        return null;
      }
      return result;
    } catch (err) {
      setError("Failed to get consumer by email");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Create a consumer
  const createConsumer = useCallback(async (data) => {
    setLoading(true);
    setError(null);
    try {
      const result = await consumersApi.createConsumer(data);
      if (result.error) {
        setError(result.error);
        return null;
      }
      return result;
    } catch (err) {
      setError("Failed to create consumer");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Update a consumer
  const updateConsumer = useCallback(async (id, data) => {
    setLoading(true);
    setError(null);
    try {
      const result = await consumersApi.updateConsumer(id, data);
      if (result.error) {
        setError(result.error);
        return null;
      }
      return result;
    } catch (err) {
      setError("Failed to update consumer");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Delete a consumer
  const deleteConsumer = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const result = await consumersApi.deleteConsumer(id);
      if (result.error) {
        setError(result.error);
        return null;
      }
      return result;
    } catch (err) {
      setError("Failed to delete consumer");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);


  // Add credits to a consumer
  const updateCredits = useCallback(async (id, data) => {
    setLoading(true);
    setError(null);
    try {
      const result = await consumersApi.updateCredit(id, data);
      if (result.error) {
        setError(result.error);
        return null;
      }
      return result;
    } catch (err) {
      setError("Failed to update credits");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);


  // Get consumer credit balance
  const getCreditBalance = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const result = await consumersApi.getCreditBalance(id);
      if (result.error) {
        setError(result.error);
        return null;
      }
      return result;
    } catch (err) {
      setError("Failed to get credit balance");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Get consumer credit ledger
  const getCreditLedger = useCallback(async (id, count = 20) => {
    setLoading(true);
    setError(null);
    try {
      const result = await consumersApi.getCreditLedger(id, count);
      if (result.error) {
        setError(result.error);
        return null;
      }
      return result;
    } catch (err) {
      setError("Failed to get credit ledger");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Validate consumer credits
  const validateCredits = useCallback(async (id, requiredCredits = 1) => {
    setLoading(true);
    setError(null);
    try {
      const result = await consumersApi.validateCredits(id, requiredCredits);
      if (result.error) {
        setError(result.error);
        return null;
      }
      return result;
    } catch (err) {
      setError("Failed to validate credits");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Validate consumer credits
  const getConversationByConsumerID = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const result = await consumersApi.getConversationByConsumerID(id);
      if (result.error) {
        setError(result.error);
        return null;
      }
      return result;
    } catch (err) {
      setError("Failed to validate credits");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);
  const getConversationWithCreditsByConsumerID = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const result = await consumersApi.getConversationWithCreditsByConsumerID(
        id
      );
      if (result.error) {
        setError(result.error);
        return null;
      }
      return result;
    } catch (err) {
      setError("Failed to validate credits");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Get notes
  const getConsumerNotesByConsumerID = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const result = await consumersApi.getConsumerNotesByConsumerID(id);
      if (result.error) {
        setError(result.error);
        setNotes([]);
        return null;
      }
      setNotes(result);
      return result;
    } catch (err) {
      setError("Failed to fetch consumer notes");
      setNotes([]);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Add note
  const addNoteToConsumer = useCallback(async (id, note) => {
    setLoading(true);
    setError(null);
    try {
      const result = await consumersApi.addNoteToConsumer(id, note);
      if (result.error) {
        setError(result.error);
        return null;
      }
      setNotes((prev) => [...prev, result]);
      return result;
    } catch (err) {
      setError("Failed to add note");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Update note
  const updateConsumerNote = useCallback(async (id, noteId, updatedNote) => {
    setLoading(true);
    setError(null);
    try {
      const result = await consumersApi.updateConsumerNote(
        id,
        noteId,
        updatedNote
      );
      if (result.error) {
        setError(result.error);
        return null;
      }
      setNotes((prev) =>
        prev.map((note) =>
          note.id === noteId ? { ...note, ...updatedNote } : note
        )
      );
      return result;
    } catch (err) {
      setError("Failed to update note");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Delete note
  const deleteConsumerNote = useCallback(async (id, noteId) => {
    setLoading(true);
    setError(null);
    try {
      const result = await consumersApi.deleteConsumerNote(id, noteId);
      if (result.error) {
        setError(result.error);
        return null;
      }
      setNotes((prev) => prev.filter((note) => note.id !== noteId));
      return result;
    } catch (err) {
      setError("Failed to delete note");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const [fields, setFields] = useState([]);

  // Custom Fields Management
  const getConsumerCustomFields = useCallback(async (consumerId) => {
    setLoading(true);
    setError(null);
    try {
      const result = await consumersApi.getConsumerCustomFields(consumerId);
      if (result.error) {
        setError(result.error);
        return null;
      }
      setFields(result); // assuming setFields is your state setter
      return result;
    } catch (err) {
      setError("Failed to fetch custom fields");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const getConsumerCustomFieldById = useCallback(async (fieldId) => {
    setLoading(true);
    setError(null);
    try {
      const result = await consumersApi.getConsumerCustomFieldById(fieldId);
      if (result.error) {
        setError(result.error);
        return null;
      }
      return result;
    } catch (err) {
      setError("Failed to fetch custom field");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

const getConsumerAcknowledgement = useCallback(async (clientId) => {
      setLoading(true);
      setError(null);
      try {
        const result = await consumersApi.getConsumerAcknowledgement(clientId);
        if (result.error) {
          setError(result.error);
          return null;
        }
        return result;
      } catch (err) {
        setError("Failed to fetch acknowledgement");
        return null;
      } finally {
        setLoading(false);
      }
    }, []);
  

  // const createOrUpdateConsumerAcknowledgement = useCallback(async (consumerId, payload) => {
  //   setLoading(true);
  //   setError(null);
  //   try {
  //     const result = await consumersApi.createOrUpdateConsumerAcknowledgement(consumerId, payload);
  //     if (result.error) {
  //       setError(result.error);
  //       return null;
  //     }
  //     return result;
  //   } catch (err) {
  //     setError("Failed to save acknowledgement");
  //     return null;
  //   } finally {
  //     setLoading(false);
  //   }
  // }, []);

  const createConsumerCustomField = useCallback(
    async (consumerId, fieldData) => {
      setLoading(true);
      setError(null);
      try {
        const result = await consumersApi.createConsumerCustomField(
          consumerId,
          fieldData
        );
        if (result.error) {
          setError(result.error);
          return null;
        }
        setFields((prev) => [...prev, result]);
        return result;
      } catch (err) {
        setError("Failed to create custom field");
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const updateConsumerCustomField = useCallback(async (fieldId, fieldData) => {
    setLoading(true);
    setError(null);
    try {
      const result = await consumersApi.updateConsumerCustomField(
        fieldId,
        fieldData
      );
      if (result.error) {
        setError(result.error);
        return null;
      }
      setFields((prev) =>
        prev.map((field) =>
          field.id === fieldId ? { ...field, ...fieldData } : field
        )
      );
      return result;
    } catch (err) {
      setError("Failed to update custom field");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteConsumerCustomField = useCallback(async (fieldId) => {
    setLoading(true);
    setError(null);
    try {
      const result = await consumersApi.deleteConsumerCustomField(fieldId);
      if (result.error) {
        setError(result.error);
        return null;
      }
      setFields((prev) => prev.filter((field) => field.id !== fieldId));
      return result;
    } catch (err) {
      setError("Failed to delete custom field");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const searchConsumers = useCallback(async (payload) => {
    setLoading(true);
    setError(null);
    try {
      const result = await consumersApi.searchConsumers(payload);
      if (result.error) {
        setError(result.error);
        return null;
      }
      return result;
    } catch (err) {
      setError("Failed to search consumers");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    error,
    notes,
    fields,
    getConsumerById,
    getConsumersByClient,
    getConsumerByEmail,
    createConsumer,
    updateConsumer,
    deleteConsumer,
    updateCredits,
    getCreditBalance,
    getCreditLedger,
    validateCredits,
    getConversationByConsumerID,
    getConversationWithCreditsByConsumerID,
    getConsumerNotesByConsumerID,
    addNoteToConsumer,
    updateConsumerNote,
    deleteConsumerNote,
    getConsumerCustomFields,
    getConsumerCustomFieldById,
    createConsumerCustomField,
    updateConsumerCustomField,
    deleteConsumerCustomField,
    getConsumerAcknowledgement,
    // createOrUpdateConsumerAcknowledgement,
    searchConsumers,
    updateConsumerNote,
  };
};
